'use client';

import type { ToggleEventHandler } from 'react';
import { useState } from 'react';

import { Button } from '#/components/Button/Button';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { Icon } from '#/components/Icon/Icon';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import {
  LayoutHeaderPanelBody,
  LayoutHeaderPanelHeader,
  LayoutHeaderPanelHolder,
  LayoutHeaderPanelSectionTitle,
} from '#/layouts/Layout/Header/Panel/LayoutHeaderPanel.styled';
import { LayoutHeaderPanelLogoutRow } from '#/layouts/Layout/Header/Panel/LayoutHeaderPanelLogoutRow';
import { isAdmin } from '#/utils/userEnvironment';

import { hamburgerMenuDataByLocale } from './data/hamburgerMenuDataByLocale';
import type {
  HamburgerMenuExpandableRowType,
  HamburgerMenuSection,
  HamburgerMenuSectionType,
} from './data/types/HamburgerMenuData';
import {
  HamburgerMenuHolder,
  HamburgerMenuRowsHolder,
  HamburgerMenuSectionHolder,
} from './HamburgerMenu.styled';
import { hamburgerMenuTrackingName } from './hamburgerMenuConstants';
import { HamburgerMenuMyAccountSection } from './HamburgerMenuMyAccountSection';
import { HamburgerMenuOrganizationSection } from './HamburgerMenuOrganizationSection';
import { HamburgerMenuRow } from './HamburgerMenuRow';

type Props = {
  locale: I18nLocale;
};

// Admins get their own sections first, so Create moves up to sit next to them
const adminSectionOrder: HamburgerMenuSectionType[] = ['create', 'explore'];

const emptyExpandedRowTypes: ReadonlySet<HamburgerMenuExpandableRowType> =
  new Set();

export function HamburgerMenu({ locale }: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();

  const [expandedRowTypes, setExpandedRowTypes] = useState<
    ReadonlySet<HamburgerMenuExpandableRowType>
  >(emptyExpandedRowTypes);

  const {
    sections,
    accountSectionTitle,
    myAccountSectionTitle,
    organizationSectionTitle,
    organizationsSectionTitle,
  } = hamburgerMenuDataByLocale[locale];

  const toggleExpanded = (type: HamburgerMenuExpandableRowType) => {
    setExpandedRowTypes((current) => {
      const next = new Set(current);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // The panel always opens collapsed: reset expandable rows on close
  const onToggle: ToggleEventHandler<HTMLDetailsElement> = (event) => {
    if (event.currentTarget.open) return;
    setExpandedRowTypes(emptyExpandedRowTypes);
  };

  const renderSection = (section: HamburgerMenuSection) => (
    <HamburgerMenuSectionHolder
      key={section.type}
      data-qa-id={`hamburger-menu-section-${section.type}`}
    >
      <LayoutHeaderPanelSectionTitle>
        {section.title}
      </LayoutHeaderPanelSectionTitle>

      <HamburgerMenuRowsHolder>
        {section.rows.map((row) => (
          <HamburgerMenuRow
            key={row.type}
            row={row}
            trackingContext={hamburgerMenuTrackingName}
            expandedRowTypes={expandedRowTypes}
            onToggleExpanded={toggleExpanded}
          />
        ))}
      </HamburgerMenuRowsHolder>
    </HamburgerMenuSectionHolder>
  );

  const renderBody = () => {
    if (!user) return sections.map(renderSection);

    if (!isAdmin(user)) {
      return (
        <>
          {sections.map(renderSection)}

          <HamburgerMenuSectionHolder data-qa-id="hamburger-menu-section-account">
            <LayoutHeaderPanelSectionTitle>
              {accountSectionTitle}
            </LayoutHeaderPanelSectionTitle>

            <HamburgerMenuRowsHolder>
              <LayoutHeaderPanelLogoutRow qaId="hamburger-menu-logout-link" />
            </HamburgerMenuRowsHolder>
          </HamburgerMenuSectionHolder>
        </>
      );
    }

    return (
      <>
        <HamburgerMenuOrganizationSection
          user={user}
          title={
            user.orgs.length === 1
              ? organizationSectionTitle
              : organizationsSectionTitle
          }
          expandedRowTypes={expandedRowTypes}
          onToggleExpanded={toggleExpanded}
        />

        <HamburgerMenuMyAccountSection
          user={user}
          title={myAccountSectionTitle}
        />

        {adminSectionOrder.flatMap((type) =>
          sections
            .filter((section) => section.type === type)
            .map(renderSection),
        )}

        <HamburgerMenuSectionHolder data-qa-id="hamburger-menu-section-logout">
          <HamburgerMenuRowsHolder>
            <LayoutHeaderPanelLogoutRow qaId="hamburger-menu-logout-link" />
          </HamburgerMenuRowsHolder>
        </HamburgerMenuSectionHolder>
      </>
    );
  };

  return (
    <HamburgerMenuHolder data-qa-id="hamburger-menu">
      <Dropdown
        trigger={
          <LayoutHeaderButton
            variant="grey"
            data={{ type: 'inert' }}
            qaId="hamburger-menu-toggle"
            ariaLabel={i18n('Menu')}
            smallHorizontalPaddingOnMobile
          >
            <Icon name="hamburger" size={24} />
          </LayoutHeaderButton>
        }
        // Open only: rich-details doesn't track closing
        trackClickedOpen={{ name: hamburgerMenuTrackingName }}
        onToggle={onToggle}
      >
        <LayoutHeaderPanelHolder data-qa-id="hamburger-menu-panel">
          <LayoutHeaderPanelHeader>
            <Button
              type="button"
              aria-label={i18n('Close menu')}
              variant="tertiary-grey"
              icon={{ name: 'close' }}
              qaId="hamburger-menu-close"
              clickActions={{ closeRichDetailsFromWithin: true }}
            />
          </LayoutHeaderPanelHeader>

          <LayoutHeaderPanelBody>{renderBody()}</LayoutHeaderPanelBody>
        </LayoutHeaderPanelHolder>
      </Dropdown>
    </HamburgerMenuHolder>
  );
}
